import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import * as St from './index.styled';
import { CustomDialog } from '../dialog/v1/CustomDialog';
import CustomBlockButton from '../button/CustomBlockButton';
import { useProductOptionsQueryHook } from '../../hooks/useProductOptionsQueryHook';
import { useModalBackClose } from '../../hooks/useModalBackClose';

/**
 * 상품(product-option) 선택 모달.
 * 서버에서 product-options 목록을 react-query로 불러와 검색/선택할 수 있게 한다.
 * 빠른채움 등 상품 자동 입력이 필요한 여러 화면에서 공용으로 사용한다.
 * 모바일 사용을 우선해 큰 터치 타깃 / 세로 스택 / 고정 검색·하단바로 구성한다.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} [props.onSelect] - (단일/연속 모드) 선택된 productOption 객체를 전달
 * @param {boolean} [props.closeOnSelect=true] - 선택 시 모달을 닫을지 여부. false면 연속 선택 가능
 * @param {boolean} [props.multiSelect=false] - 체크박스 다중 선택 모드(전체 선택 포함)
 * @param {function} [props.onSelectMany] - (다중 모드) 확인 시 선택된 productOption 배열을 전달
 * @param {string} [props.title='상품 선택']
 */
export function ProductOptionPickerModal({
    open,
    onClose,
    onSelect,
    closeOnSelect = true,
    multiSelect = false,
    onSelectMany,
    title = '상품 선택',
}) {
    const isMobile = useMediaQuery('(max-width:600px)');

    const [keyword, setKeyword] = useState('');
    const [addedCount, setAddedCount] = useState(0);
    const [lastAddedId, setLastAddedId] = useState(null);
    // 다중 선택 모드: 선택한 항목을 id→객체로 보관 (검색어를 바꿔도 유지)
    const [selectedMap, setSelectedMap] = useState(() => new Map());

    // 뒤로가기(popstate)로 모달 닫기
    useModalBackClose(open, onClose);

    // open 상태일 때만 조회 (모달 닫힘 시 불필요한 호출 방지)
    const { productOptions, isFetching, isError } = useProductOptionsQueryHook({ enabled: open });

    // 모달이 열릴 때마다 검색어 / 선택 상태 초기화
    useEffect(() => {
        if (open) {
            setKeyword('');
            setAddedCount(0);
            setLastAddedId(null);
            setSelectedMap(new Map());
        }
    }, [open]);

    const filteredProductOptions = useMemo(() => {
        const trimmed = keyword.trim().toLowerCase();
        if (!trimmed) {
            return productOptions;
        }

        return productOptions?.filter(po => {
            const target = [
                po?.productName,
                po?.productOptionName,
                po?.productOptionCode,
            ].join(' ').toLowerCase();

            return target.includes(trimmed);
        });
    }, [productOptions, keyword]);

    const resultCount = filteredProductOptions?.length ?? 0;

    const handleSelect = (productOption) => {
        onSelect?.(productOption);

        if (closeOnSelect) {
            onClose();
            return;
        }

        // 연속 추가 모드: 방금 추가한 항목을 잠시 강조 + 카운트 증가
        setAddedCount(prev => prev + 1);
        setLastAddedId(productOption?.productOptionId);
    };

    // ── 다중 선택 모드 ──
    const toggleSelect = (productOption) => {
        const id = productOption?.productOptionId;
        setSelectedMap(prev => {
            const next = new Map(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.set(id, productOption);
            }
            return next;
        });
    };

    const allFilteredSelected = resultCount > 0 && filteredProductOptions.every(po => selectedMap.has(po?.productOptionId));

    const toggleSelectAll = () => {
        setSelectedMap(prev => {
            const next = new Map(prev);
            if (allFilteredSelected) {
                filteredProductOptions.forEach(po => next.delete(po?.productOptionId));
            } else {
                filteredProductOptions.forEach(po => next.set(po?.productOptionId, po));
            }
            return next;
        });
    };

    const selectedCount = selectedMap.size;

    const handleConfirmMany = () => {
        if (selectedCount > 0) {
            onSelectMany?.(Array.from(selectedMap.values()));
        }
        onClose();
    };

    return (
        <CustomDialog
            open={open}
            fullScreen={isMobile}
            maxWidth='sm'
        >
            <St.Container className={isMobile ? 'mobile' : ''}>
                <St.HeaderBar>
                    <CustomDialog.Header>
                        <CustomDialog.Header.Fake />
                        <CustomDialog.Header.Title>{title}</CustomDialog.Header.Title>
                        <CustomDialog.Header.Close onClick={() => onClose()} />
                    </CustomDialog.Header>
                </St.HeaderBar>

                <St.SearchBox>
                    <div className='inputWrap'>
                        <input
                            type='text'
                            inputMode='search'
                            placeholder='상품명 / 옵션명 / 옵션코드 검색'
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            autoFocus={!isMobile}
                        />
                        {keyword &&
                            <button
                                type='button'
                                className='clearButton'
                                onClick={() => setKeyword('')}
                                aria-label='검색어 지우기'
                            >
                                ✕
                            </button>
                        }
                    </div>
                    <div className='resultMeta'>
                        {multiSelect
                            ? (
                                <>
                                    <label className='selectAll'>
                                        <input
                                            type='checkbox'
                                            checked={allFilteredSelected}
                                            onChange={toggleSelectAll}
                                            disabled={resultCount <= 0}
                                        />
                                        전체 선택{keyword.trim() ? ' (검색결과)' : ''}
                                    </label>
                                    <span className='addedCount'>{selectedCount}개 선택됨</span>
                                </>
                            )
                            : (
                                <>
                                    <span>{isFetching ? '불러오는 중…' : `${resultCount}개`}</span>
                                    {!closeOnSelect && addedCount > 0 &&
                                        <span className='addedCount'>이번에 {addedCount}개 추가됨</span>
                                    }
                                </>
                            )
                        }
                    </div>
                </St.SearchBox>

                <St.ListWrapper>
                    {isFetching &&
                        <St.EmptyBox>불러오는 중...</St.EmptyBox>
                    }
                    {!isFetching && isError &&
                        <St.EmptyBox>상품을 불러오지 못했습니다.</St.EmptyBox>
                    }
                    {!isFetching && !isError && resultCount <= 0 &&
                        <St.EmptyBox>표시할 상품이 없습니다.</St.EmptyBox>
                    }
                    {!isFetching && !isError && filteredProductOptions?.map(po => {
                        const isSelected = multiSelect && selectedMap.has(po?.productOptionId);
                        const isJustAdded = !multiSelect && !closeOnSelect && lastAddedId === po?.productOptionId;

                        return (
                            <St.ListItem
                                key={po?.productOptionId}
                                type='button'
                                className={isSelected ? 'selected' : (isJustAdded ? 'justAdded' : '')}
                                onClick={() => multiSelect ? toggleSelect(po) : handleSelect(po)}
                            >
                                {multiSelect &&
                                    <span className='checkbox'>{isSelected ? '✓' : ''}</span>
                                }
                                <div className='info'>
                                    <div className='productName'>{po?.productName || '-'}</div>
                                    <div className='subLine'>
                                        <span className='optionName'>{po?.productOptionName || '-'}</span>
                                        {po?.productOptionCode &&
                                            <span className='optionCode'>{po?.productOptionCode}</span>
                                        }
                                    </div>
                                </div>
                                {!multiSelect &&
                                    <div className='action'>
                                        {isJustAdded ? '추가됨' : (closeOnSelect ? '선택' : '추가')}
                                    </div>
                                }
                            </St.ListItem>
                        );
                    })}
                </St.ListWrapper>

                <St.Footer>
                    {multiSelect
                        ? (
                            <CustomBlockButton
                                type='button'
                                className='closeButton'
                                onClick={() => handleConfirmMany()}
                            >
                                {selectedCount > 0 ? `${selectedCount}개 추가` : '닫기'}
                            </CustomBlockButton>
                        )
                        : (
                            <CustomBlockButton
                                type='button'
                                className='closeButton'
                                onClick={() => onClose()}
                            >
                                {!closeOnSelect && addedCount > 0 ? `완료 (${addedCount}개 추가)` : '닫기'}
                            </CustomBlockButton>
                        )
                    }
                </St.Footer>
            </St.Container>
        </CustomDialog>
    );
}
